import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowLeft, Check, ChevronDown, ChevronUp, UserPlus } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { agentsApi } from '../../services/agentsApi';
import { partnerSessionsApi } from '../../services/partnerSessionsApi';
import type { IntakePersonDetail, IntakePersonProgress, IntakeSection, PersonSession } from '../../types/aai';
import { Action, Chip, Field, Input, Kicker, Loading, Modal, Select } from '../../components/aa';
import { ExportAction } from './sessions/ExportAction';
import { shortDate } from './sessions/sessionShared';

/*
 * The people of one company: who is in it, how far each is through the
 * intake, and an invite that emails them their link. Opening a person shows
 * what they answered in their journey and in the partner's sessions, what is
 * left and the tools they use.
 */

type Member = { id: string; email: string; name: string | null; role: string; department?: string | null; is_consultant?: boolean; is_platform_team?: boolean };
type Department = { id: string; name: string };

const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Owner',
  EXECUTIVE: 'Executive',
  AI_CHAMPION: 'AI Champion',
  DEPARTMENT_HEAD: 'Department Head',
  TEAM_LEAD: 'Team Lead',
  AI_PARTICIPANT: 'Employee',
};

const STEP_LABEL: Record<string, string> = {
  welcome: 'Welcome', about: 'About you', apps: 'Apps', permissions: 'Permissions', ideas: 'Ideas', connect: 'Connect', start: 'Start',
};

const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
};

const roleLabel = (role: string): string => ROLE_LABEL[(role || '').toUpperCase()] || role || 'Member';

const percent = (answered: number, total: number): number => {
  if (!total) return 0;
  return Math.round((answered / total) * 100);
};

const setupLine = (p?: IntakePersonProgress): string => {
  if (!p || !p.step) return 'Setup not started';
  if (p.finished) return 'Setup done';
  return `In setup · ${STEP_LABEL[p.step] || p.step}`;
};

const Bar: React.FC<{ value: number }> = ({ value }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-canvas-inset">
    <div className="h-full rounded-full bg-[#ffea00]" style={{ width: `${value}%` }} />
  </div>
);

const InviteModal: React.FC<{ clientId: string; departments: Department[]; onClose: () => void; onDone: () => void }> = ({ clientId, departments, onClose, onDone }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('AI_PARTICIPANT');
  const [dept, setDept] = useState('');
  const [busy, setBusy] = useState(false);

  const invite = async () => {
    const address = email.trim().toLowerCase();
    if (!address.includes('@')) { toast.info('A work email is needed.'); return; }
    setBusy(true);
    try {
      const { data } = await agentsApi.addWorkspaceMember(clientId, {
        email: address, name: name.trim() || undefined, role, department_id: dept || undefined, send_email: true,
      });
      if (data?.email_sent === false) toast.success(`${address} added. The invite email could not be sent.`);
      if (data?.email_sent !== false) toast.success(`Invite sent to ${address}.`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not invite the person.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      size="dialog"
      onClose={onClose}
      context={<Kicker>People</Kicker>}
      footer={<div className="flex justify-end gap-2"><Action variant="ghost" onClick={onClose}>Cancel</Action><Action onClick={invite} disabled={busy} reason="Sending">Send invite</Action></div>}
    >
      <h2 className="m-0 text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">Invite a person</h2>
      <p className="m-0 mt-2 text-[14px] leading-[23px] text-ink-muted">They get an email with their link to sign in, then walk the setup and the intake.</p>
      <div className="mt-6 space-y-5">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" /></Field>
        <Field label="Work email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" /></Field>
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="AI_PARTICIPANT">Employee</option>
            <option value="DEPARTMENT_HEAD">Department Head</option>
            <option value="AI_CHAMPION">AI Champion</option>
          </Select>
        </Field>
        <Field label="Department">
          <Select value={dept} onChange={(e) => setDept(e.target.value)}>
            <option value="">Not placed yet</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
        </Field>
      </div>
    </Modal>
  );
};

/* A section of questions written for this one person, sent to their email
   and added to their intake after everything else. */
const whenLine = (s: IntakeSection): string => {
  if (s.fixed) return 'During initial setup';
  return 'After setup';
};

const SectionCard: React.FC<{
  section: IntakeSection;
  line: string;
  shown: boolean;
  onToggle: () => void;
}> = ({ section: s, line, shown, onToggle }) => {
  const answered = s.questions.filter((q) => (q.answer || '').trim()).length;
  let tone: 'ok' | 'neutral' = 'neutral';
  if (answered === s.questions.length) tone = 'ok';
  const chip = { label: `${answered} of ${s.questions.length} answered`, tone };
  let headBorder = '';
  if (shown) headBorder = 'border-b border-line-soft';
  let toggleWord = 'Open';
  let ToggleIcon = ChevronDown;
  if (shown) {
    toggleWord = 'Close';
    ToggleIcon = ChevronUp;
  }
  return (
    <div className="rounded-[14px] border border-line bg-white">
      <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${headBorder}`}>
        <button type="button" aria-expanded={shown} onClick={onToggle} className="min-w-0 flex-1 text-left">
          <strong className="text-[15px] text-ink">{s.title}</strong>
          <p className="m-0 text-[12px] text-ink-dim">{line}</p>
        </button>
        <span className="flex items-center gap-2">
          <Chip tone={chip.tone}>{chip.label}</Chip>
          <button type="button" onClick={onToggle} className="inline-flex items-center gap-1 rounded-full border border-line-strong bg-white px-3 py-1.5 text-[11.5px] font-semibold text-ink whitespace-nowrap">
            {toggleWord}
            <ToggleIcon size={13} />
          </button>
        </span>
      </div>
      {shown && (
        <ul className="m-0 list-none space-y-3 px-5 py-4">
          {s.questions.map((q) => {
            const answer = (q.answer || '').trim();
            let mark = 'border border-line-strong';
            if (answer) mark = 'bg-ink text-white';
            return (
              <li key={q.id} className="flex gap-3">
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${mark}`}>
                  {answer && <Check size={12} />}
                </span>
                <div className="min-w-0">
                  <p className="m-0 text-[13.5px] font-semibold text-ink">{q.label}</p>
                  {answer && <p className="m-0 mt-0.5 whitespace-pre-wrap text-[13px] text-ink-muted">{answer}</p>}
                  {!answer && <p className="m-0 mt-0.5 text-[13px] text-ink-dim">Not answered yet</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const SessionCard: React.FC<{ session: PersonSession; shown: boolean; onToggle: () => void }> = ({ session: s, shown, onToggle }) => {
  let chip = <Chip tone="neutral">Waiting</Chip>;
  if (s.answered_at) chip = <Chip tone="ok">Answered {shortDate(s.answered_at)}</Chip>;
  let headBorder = '';
  if (shown) headBorder = 'border-b border-line-soft';
  let toggleWord = 'Open';
  let ToggleIcon = ChevronDown;
  if (shown) {
    toggleWord = 'Close';
    ToggleIcon = ChevronUp;
  }
  return (
    <div className="rounded-[14px] border border-line bg-white">
      <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${headBorder}`}>
        <button type="button" aria-expanded={shown} onClick={onToggle} className="min-w-0 flex-1 text-left">
          <strong className="text-[15px] text-ink">{s.title || 'Untitled session'}</strong>
          <p className="m-0 text-[12px] text-ink-dim">Session · sent {shortDate(s.sent_at)}</p>
        </button>
        <span className="flex items-center gap-2">
          {chip}
          <button type="button" onClick={onToggle} className="inline-flex items-center gap-1 rounded-full border border-line-strong bg-white px-3 py-1.5 text-[11.5px] font-semibold text-ink whitespace-nowrap">
            {toggleWord}
            <ToggleIcon size={13} />
          </button>
        </span>
      </div>
      {shown && (
        <ul className="m-0 list-none space-y-3 px-5 py-4">
          {s.questions.map((q) => (
            <li key={q.id}>
              <p className="m-0 text-[13.5px] font-semibold text-ink">{q.label}</p>
              {q.text && <p className="m-0 mt-0.5 whitespace-pre-wrap text-[13px] text-ink-muted">{q.text}</p>}
              {!q.text && <p className="m-0 mt-0.5 text-[13px] text-ink-dim">Not answered yet</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const GroupHead: React.FC<{ title: string; sub: string }> = ({ title, sub }) => (
  <div className="pt-2">
    <h3 className="m-0 text-[15px] font-semibold text-ink">{title}</h3>
    <p className="m-0 text-[12.5px] text-ink-dim">{sub}</p>
  </div>
);

const PersonDetail: React.FC<{ clientId: string; member: Member; onBack: () => void }> = ({ clientId, member, onBack }) => {
  const [detail, setDetail] = useState<IntakePersonDetail | null>(null);
  const [sessions, setSessions] = useState<PersonSession[]>([]);
  const [error, setError] = useState('');
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setOpenIds((now) => {
    const next = new Set(now);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const load = useCallback(() => {
    aaiApi.intakePerson(clientId, member.email)
      .then((r) => setDetail(r.data))
      .catch((err) => setError(errorText(err, 'Could not load this person.')));
    partnerSessionsApi.personSessions(clientId, member.email)
      .then((r) => setSessions(r.data.sessions))
      .catch(() => setSessions([]));
  }, [clientId, member.email]);
  useEffect(() => { load(); }, [load]);


  let body: React.ReactNode = <Loading label="Loading their intake" />;
  if (error) body = <div className="text-[13px] text-danger">{error}</div>;
  if (detail) {
    const done = percent(detail.answered, detail.total);
    let setupState = 'Not started';
    if (detail.setup.started) setupState = `In progress · ${STEP_LABEL[detail.setup.step || ''] || detail.setup.step}`;
    if (detail.setup.finished_at) setupState = `Done · ${new Date(detail.setup.finished_at).toLocaleDateString()}`;
    const skipped = detail.setup.skipped.map((s) => STEP_LABEL[s] || s);
    body = (
      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-[14px] border border-line bg-white p-5">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-dim">Intake</p>
            <p className="m-0 mt-2 text-[24px] font-bold text-ink">{done}%</p>
            <p className="m-0 mb-3 text-[12.5px] text-ink-dim">{detail.answered} of {detail.total} questions answered · {detail.total - detail.answered} left</p>
            <Bar value={done} />
          </div>
          <div className="rounded-[14px] border border-line bg-white p-5">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-dim">Setup</p>
            <p className="m-0 mt-2 text-[16px] font-semibold text-ink">{setupState}</p>
            {skipped.length > 0 && <p className="m-0 mt-1 text-[12.5px] text-ink-dim">Skipped: {skipped.join(', ')}</p>}
          </div>
          <div className="rounded-[14px] border border-line bg-white p-5">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-dim">Tools they use</p>
            {detail.apps.length === 0 && <p className="m-0 mt-2 text-[13px] text-ink-dim">None picked yet.</p>}
            {detail.apps.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">{detail.apps.map((a) => <Chip key={a.slug} tone="neutral">{a.label}</Chip>)}</div>
            )}
          </div>
        </div>
        <GroupHead title="Journey" sub="The intake questions their role answers through Orbit and the AI Champion." />
        {detail.sections.map((s) => (
          <SectionCard key={s.id} section={s} line={whenLine(s)} shown={openIds.has(s.id)} onToggle={() => toggle(s.id)} />
        ))}
        <GroupHead title="Sessions" sub="Questions you sent them as a session." />
        {sessions.length === 0 && (
          <div className="rounded-[14px] border border-dashed border-line px-5 py-5 text-center text-[13px] text-ink-dim">No sessions sent to them yet.</div>
        )}
        {sessions.map((s) => (
          <SessionCard key={s.id} session={s} shown={openIds.has(`session:${s.id}`)} onToggle={() => toggle(`session:${s.id}`)} />
        ))}
      </div>
    );
  }

  return (
    <div>
      <button type="button" onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink-muted hover:text-ink">
        <ArrowLeft size={14} /> All people
      </button>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="m-0 text-[22px] font-bold text-ink">{member.name || member.email}</h2>
          <p className="m-0 mt-1 text-[13px] text-ink-dim">{[member.email, roleLabel(member.role), member.department].filter(Boolean).join(' · ')}</p>
        </div>
        <ExportAction label="Export CSV" fileName={`${member.email}-responses`} load={() => partnerSessionsApi.exportResponses(clientId, member.email)} />
      </div>
      {body}
    </div>
  );
};

export const PeoplePane: React.FC<{ clientId: string; initialEmail?: string }> = ({ clientId, initialEmail }) => {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [progress, setProgress] = useState<Record<string, IntakePersonProgress>>({});
  const [error, setError] = useState('');
  const [inviting, setInviting] = useState(false);
  const [open, setOpen] = useState<Member | null>(null);

  const load = useCallback(async () => {
    try {
      const [ws, pr] = await Promise.all([agentsApi.getWorkspace(clientId), aaiApi.intakePeople(clientId)]);
      setMembers(ws.data.members.filter((m) => !m.is_consultant && !m.is_platform_team));
      setDepartments(ws.data.departments || []);
      setProgress(pr.data.people);
    } catch (err) {
      setError(errorText(err, 'Could not load the people.'));
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const wanted = (initialEmail || '').toLowerCase();
  useEffect(() => {
    if (!wanted || !members) return;
    const match = members.find((m) => m.email.toLowerCase() === wanted);
    if (match) setOpen(match);
  }, [wanted, members]);

  if (open) return <PersonDetail clientId={clientId} member={open} onBack={() => setOpen(null)} />;
  if (error) return <div className="text-[13px] text-danger">{error}</div>;
  if (!members) return <Loading label="Loading the people" />;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="m-0 text-[20px] font-semibold text-ink">People</h2>
          <p className="m-0 mt-1 text-[13px] text-ink-dim">Everyone in the company, and how far each is through the intake.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportAction label="Export all responses" fileName="all-responses" load={() => partnerSessionsApi.exportResponses(clientId)} />
          <Action icon={<UserPlus size={15} />} onClick={() => setInviting(true)}>Invite person</Action>
        </div>
      </div>
      {members.length === 0 && (
        <div className="rounded-[14px] border border-dashed border-line px-5 py-6 text-center text-[13.5px] text-ink-dim">
          Nobody here yet. Invite someone and they get an email with their link.
        </div>
      )}
      {members.length > 0 && (
        <div className="overflow-hidden rounded-[14px] border border-line bg-white">
          {members.map((m) => {
            const p = progress[m.email.toLowerCase()];
            let done = 0;
            if (p) done = percent(p.answered, p.total);
            return (
              <button
                key={m.id || m.email}
                type="button"
                onClick={() => setOpen(m)}
                className="grid w-full grid-cols-1 items-center gap-3 border-t border-line-soft px-5 py-4 text-left first:border-t-0 hover:bg-canvas sm:grid-cols-[1.4fr_1fr_1fr]"
              >
                <span className="min-w-0">
                  <b className="block truncate text-[14px] text-ink">{m.name || m.email}</b>
                  <small className="block truncate text-[12px] text-ink-dim">{[m.email, roleLabel(m.role), m.department].filter(Boolean).join(' · ')}</small>
                </span>
                <span className="text-[12.5px] text-ink-muted">{setupLine(p)}</span>
                <span className="flex items-center gap-3">
                  <Bar value={done} />
                  <span className="w-10 shrink-0 text-right text-[12.5px] font-semibold text-ink">{done}%</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {inviting && <InviteModal clientId={clientId} departments={departments} onClose={() => setInviting(false)} onDone={load} />}
    </div>
  );
};

export default PeoplePane;
